import React, { Suspense, lazy } from "react";
import { motion } from "framer-motion";
import { ArrowDown, MapPin, Download } from "lucide-react";
import { PROFILE } from "@/data/content";

const Hero3DCanvas = lazy(() => import("@/components/site/Hero3DCanvas"));

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

export default function Hero() {
  return (
    <section
      id="hero"
      className="relative min-h-screen w-full overflow-hidden flex items-center"
      data-testid="hero-section"
    >
      {/* Stadium overlay */}
      <div
        className="absolute inset-0 bg-cover bg-center opacity-[0.16] mix-blend-overlay grayscale"
        style={{ backgroundImage: `url(${PROFILE.photos.hero})` }}
      />
      {/* 3D canvas */}
      <div className="absolute inset-0 z-10">
        <Suspense fallback={<div className="w-full h-full" />}>
          <Hero3DCanvas />
        </Suspense>
      </div>

      {/* Radial vignette */}
      <div className="absolute inset-0 z-20 pointer-events-none bg-[radial-gradient(ellipse_at_center,transparent_30%,#050505_85%)]" />

      {/* Text content */}
      <div className="relative z-30 container mx-auto px-6 md:px-12 w-full pointer-events-none">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="flex items-center gap-3 mb-6"
        >
          <span className="h-px w-10 bg-volt" />
          <span className="font-mono text-xs md:text-sm tracking-[0.25em] uppercase text-volt">
            {PROFILE.tagline}
          </span>
        </motion.div>

        <h1
          className="font-heading uppercase leading-[0.82] tracking-tighter text-white text-[19vw] md:text-[13vw]"
          data-testid="hero-name"
        >
          <motion.span
            className="block"
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
          >
            Kusum
          </motion.span>
          <motion.span
            className="block text-stroke-volt"
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
          >
            Shaikh
          </motion.span>
        </h1>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="mt-8 flex flex-col md:flex-row md:items-center gap-4 md:gap-6 pointer-events-auto"
        >
          <button
            onClick={() => scrollTo("contact")}
            className="font-heading tracking-wide text-lg bg-volt text-black px-8 py-4 hover:bg-white transition-colors w-fit"
            data-testid="hero-cta"
          >
            WORK WITH ME
          </button>
          <a
            href={PROFILE.cvUrl}
            target="_blank"
            rel="noopener noreferrer"
            download
            className="font-mono uppercase tracking-wide text-sm border border-neutral-700 text-white px-7 py-4 hover:border-volt hover:text-volt transition-colors w-fit flex items-center gap-2"
            data-testid="hero-download-cv"
          >
            <Download size={16} /> Download CV
          </a>
          <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-neutral-400">
            <MapPin size={14} className="text-volt" />
            {PROFILE.location}
          </div>
        </motion.div>
      </div>

      {/* Interaction hint */}
      <div
        className="absolute bottom-8 right-6 md:right-12 z-30 hidden md:flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500 pointer-events-none"
        data-testid="hero-3d-hint"
      >
        <span className="h-1.5 w-1.5 rounded-full bg-volt animate-pulse" />
        Drag to spin · Click to kick
      </div>

      {/* Scroll indicator */}
      <motion.button
        onClick={() => scrollTo("about")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-30 flex flex-col items-center gap-2 text-neutral-500 hover:text-volt transition-colors"
        data-testid="hero-scroll-indicator"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.3em]">Scroll</span>
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.6 }}>
          <ArrowDown size={16} />
        </motion.span>
      </motion.button>
    </section>
  );
}
